"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const FLOATING_VIALS_IMAGE = "https://tektideslabs.com/wp-content/uploads/2026/10/Four-Glossy-Peptide-Vials-in-Flight.png";
export default function Hero() {
  const reduceMotion = useReducedMotion();
  return <section id="hero" data-nav="light" aria-labelledby="hero-heading" className="bg-[#f8fafb] px-4 pb-5 pt-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px]">
        <div className="relative min-h-[690px] overflow-hidden rounded-[34px] bg-[#eef5f8] sm:min-h-[720px] lg:min-h-[760px]">

          {/* Subtle blue atmosphere */}
          <div aria-hidden="true" className="pointer-events-none absolute right-[5%] top-[10%] h-[420px] w-[420px] rounded-full bg-[#18aeef]/[0.05] blur-[110px]" />

          <div className="relative z-10 grid min-h-[690px] items-center gap-10 px-7 py-16 sm:min-h-[720px] sm:px-12 lg:min-h-[760px] lg:grid-cols-[0.9fr_1.1fr] lg:px-20 lg:py-20 xl:px-24">

            {/* LEFT — HERO TEXT */}
            <motion.div initial={reduceMotion ? false : {
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: reduceMotion ? 0 : 0.65
          }} className="relative z-20 max-w-[590px]">
              <div className="mb-8 flex items-center gap-3">
                <span className="h-px w-8 bg-[#159fdf]" />

                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1479a7]">
                  TEK TIDES LABS
                </span>
              </div>

              <h1 id="hero-heading" className="max-w-[620px] text-[clamp(3.3rem,6vw,6.5rem)] font-semibold leading-[0.91] tracking-[-0.065em] text-[#0a1218]">
                Research,
                <br />
                refined.
              </h1>

              <p className="mt-8 max-w-[460px] text-base leading-7 text-[#56636a] sm:text-lg">
                Research-use products with clear product information,
                accessible documentation, and a straightforward ordering
                experience.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-5">
                <Button asChild className="group h-12 rounded-full bg-[#087db3] px-7 font-semibold text-white shadow-none hover:bg-[#066d9d]">
                  <Link to="/ShopPage">
                    Explore Products
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </Button>

                <Link to="/ContactPage" className="text-sm font-medium text-[#31515f] transition-colors hover:text-[#087db3]">
                  Request documentation ↗
                </Link>
              </div>
            </motion.div>

            {/* RIGHT — TRANSPARENT FLOATING VIALS */}
            <motion.div initial={reduceMotion ? false : {
            opacity: 0,
            x: 25
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: reduceMotion ? 0 : 0.8,
            delay: reduceMotion ? 0 : 0.1
          }} className="relative flex min-h-[320px] min-w-0 items-center justify-center sm:min-h-[420px] lg:min-h-[600px]">
              <img src={FLOATING_VIALS_IMAGE} alt="TEK TIDES LABS research vials: MOTS-C 40 MG, GLP-3 RT 30 MG, Tesamorelin 10 MG, and Sermorelin 10 MG" decoding="async" className="relative z-10 block h-auto w-full max-w-[680px] object-contain drop-shadow-[0_20px_25px_rgba(6,24,34,0.12)]" />
            </motion.div>
          </div>

          {/* BOTTOM DETAILS */}
          <div className="absolute bottom-7 left-7 right-7 z-20 flex items-end justify-between sm:left-12 sm:right-12 lg:left-20 lg:right-20 xl:left-24">
            <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-[#64737a]">
              Research Use Only
            </span>

            <span className="hidden text-[11px] font-medium text-[#64737a] sm:block">
              Explore below ↓
            </span>
          </div>

        </div>
      </div>
    </section>;
}